import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Verkaufen() {
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    address: "",
    building_type: "",
    units: "",
    condition: "",
    name: "",
    email: "",
    phone: "",
    message: "",
    consent: false,
  });

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Bitte stimmen Sie den Datenschutzbedingungen zu.");
      return;
    }
    if (!form.address || !form.name || !form.email || !form.building_type || !form.units || !form.condition) {
      toast.error("Bitte füllen Sie alle Pflichtfelder aus.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/sell-inquiry`, form);
      setDone(true);
      toast.success("Vielen Dank — Ihre Anfrage ist bei uns eingegangen.");
    } catch (err) {
      toast.error("Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="page-verkaufen-success" className="px-6 md:px-12 lg:px-24 py-32 min-h-[70vh] flex items-center">
        <div className="max-w-2xl mx-auto text-center">
          <CheckCircle2 className="h-14 w-14 text-primary mx-auto mb-8" strokeWidth={1.5} />
          <div className="text-xs uppercase tracking-[0.3em] text-primary mb-4">Anfrage gesendet</div>
          <h1 className="font-serif text-4xl sm:text-5xl font-light leading-tight">
            Vielen Dank. Wir melden uns persönlich bei Ihnen.
          </h1>
          <p className="mt-6 text-base text-muted-foreground leading-relaxed">
            Ihre Anfrage ist bei uns eingegangen. In der Regel meldet sich
            einer von uns innerhalb weniger Werktage telefonisch oder per
            E-Mail – diskret und ohne Verpflichtung.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="page-verkaufen">
      <section className="px-6 md:px-12 lg:px-24 pt-32 md:pt-40 pb-12">
        <div className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-primary mb-6">
          <span className="inline-block w-2 h-2 bg-accent" />
          Haus verkaufen
        </div>
        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter leading-[1.05] max-w-4xl">
          Bieten Sie uns Ihre <em className="italic">Immobilie</em> an.
        </h1>
        <p className="mt-8 max-w-2xl text-base sm:text-lg text-muted-foreground leading-relaxed">
          Erzählen Sie uns kurz von Ihrem Haus. Wir prüfen Ihre Anfrage
          persönlich, diskret und ohne Verpflichtung. In den meisten Fällen
          melden wir uns innerhalb weniger Werktage.
        </p>
      </section>

      <section className="px-6 md:px-12 lg:px-24 pb-32">
        <form
          onSubmit={submit}
          className="max-w-2xl space-y-10"
          data-testid="sell-form"
        >
          {/* Object section */}
          <div className="space-y-6">
            <div className="text-xs uppercase tracking-[0.25em] text-primary border-b border-border pb-3">
              Zum Objekt
            </div>

            <Field label="Adresse der Immobilie *">
              <Input
                data-testid="sell-form-address"
                value={form.address}
                onChange={update("address")}
                placeholder="Straße, Hausnummer, PLZ, Ort"
                className="rounded-none bg-transparent border-x-0 border-t-0 border-b border-border h-12 px-0 focus-visible:ring-0 focus-visible:border-primary text-base"
                required
              />
            </Field>

            <Field label="Art des Gebäudes *">
              <Select value={form.building_type} onValueChange={(v) => setForm((f) => ({ ...f, building_type: v }))}>
                <SelectTrigger
                  data-testid="sell-form-building-type"
                  className="rounded-none bg-transparent border-x-0 border-t-0 border-b border-border h-12 px-0 focus:ring-0 text-base"
                >
                  <SelectValue placeholder="Bitte wählen" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="mehrfamilienhaus">Mehrfamilienhaus</SelectItem>
                  <SelectItem value="buergerhaus">Bürgerhaus / Stadthaus</SelectItem>
                  <SelectItem value="jugendstilvilla">Jugendstilvilla</SelectItem>
                  <SelectItem value="gruenderzeit">Gründerzeithaus</SelectItem>
                  <SelectItem value="sonstiges">Sonstiges</SelectItem>
                </SelectContent>
              </Select>
            </Field>

            <Field label="Anzahl der Wohneinheiten *">
              <Select value={form.units} onValueChange={(v) => setForm((f) => ({ ...f, units: v }))}>
                <SelectTrigger
                  data-testid="sell-form-units"
                  className="rounded-none bg-transparent border-x-0 border-t-0 border-b border-border h-12 px-0 focus:ring-0 text-base"
                >
                  <SelectValue placeholder="Bitte wählen" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="1-2">1 – 2 Einheiten</SelectItem>
                  <SelectItem value="3-4">3 – 4 Einheiten</SelectItem>
                  <SelectItem value="5-6">5 – 6 Einheiten</SelectItem>
                  <SelectItem value="7+">Mehr als 6 Einheiten</SelectItem>
                </SelectContent>
              </Select>
            </Field>

            <Field label="Zustand *">
              <Select value={form.condition} onValueChange={(v) => setForm((f) => ({ ...f, condition: v }))}>
                <SelectTrigger
                  data-testid="sell-form-condition"
                  className="rounded-none bg-transparent border-x-0 border-t-0 border-b border-border h-12 px-0 focus:ring-0 text-base"
                >
                  <SelectValue placeholder="Bitte wählen" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="gut">Guter Zustand, kosmetische Auffrischung</SelectItem>
                  <SelectItem value="teil">Teilsanierungsbedarf</SelectItem>
                  <SelectItem value="voll">Vollsanierungsbedarf</SelectItem>
                  <SelectItem value="unklar">Unklar / weiß ich nicht</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>

          {/* Contact section */}
          <div className="space-y-6">
            <div className="text-xs uppercase tracking-[0.25em] text-primary border-b border-border pb-3">
              Ihre Kontaktdaten
            </div>

            <Field label="Ihr Name *">
              <Input
                data-testid="sell-form-name"
                value={form.name}
                onChange={update("name")}
                placeholder="Vor- und Nachname"
                className="rounded-none bg-transparent border-x-0 border-t-0 border-b border-border h-12 px-0 focus-visible:ring-0 focus-visible:border-primary text-base"
                required
              />
            </Field>

            <div className="grid sm:grid-cols-2 gap-6">
              <Field label="E-Mail *">
                <Input
                  data-testid="sell-form-email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="ihre@email.de"
                  className="rounded-none bg-transparent border-x-0 border-t-0 border-b border-border h-12 px-0 focus-visible:ring-0 focus-visible:border-primary text-base"
                  required
                />
              </Field>
              <Field label="Telefon">
                <Input
                  data-testid="sell-form-phone"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="optional"
                  className="rounded-none bg-transparent border-x-0 border-t-0 border-b border-border h-12 px-0 focus-visible:ring-0 focus-visible:border-primary text-base"
                />
              </Field>
            </div>

            <Field label="Nachricht">
              <Textarea
                data-testid="sell-form-message"
                value={form.message}
                onChange={update("message")}
                placeholder="Weitere Informationen zu Ihrem Haus oder Ihrer Situation."
                rows={5}
                className="rounded-none bg-transparent border border-border focus-visible:ring-0 focus-visible:border-primary text-base"
              />
            </Field>
          </div>

          <div className="flex items-start gap-3">
            <Checkbox
              id="consent"
              data-testid="sell-form-consent"
              checked={form.consent}
              onCheckedChange={(v) => setForm((f) => ({ ...f, consent: !!v }))}
              className="rounded-none border-foreground/40 data-[state=checked]:bg-primary data-[state=checked]:border-primary mt-1"
            />
            <Label htmlFor="consent" className="text-sm leading-relaxed text-muted-foreground cursor-pointer">
              Ich stimme zu, dass meine Angaben zur Bearbeitung meiner Anfrage
              gespeichert werden. Weitere Informationen entnehmen Sie unserer{" "}
              <a href="/datenschutz" className="text-primary underline underline-offset-4">Datenschutzerklärung</a>.
            </Label>
          </div>

          <Button
            type="submit"
            data-testid="sell-form-submit-button"
            disabled={submitting}
            className="rounded-none bg-primary hover:bg-primary/90 text-primary-foreground h-12 px-10 text-sm tracking-wide w-full sm:w-auto"
          >
            {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Anfrage senden
          </Button>
        </form>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
